import { LatLng } from 'leaflet';
import { OpenStreetMapProvider } from 'leaflet-geosearch';
import { RawResult } from 'leaflet-geosearch/dist/providers/openStreetMapProvider.js';
import { RequestResult } from 'leaflet-geosearch/lib/providers/openStreetMapProvider.js';
import { BoundsTuple, ParseArgument, RequestType, SearchArgument } from 'leaflet-geosearch/lib/providers/provider.js';

import { SearchResult } from 'leaflet-geosearch/dist/providers/provider.js';

export interface ErrorResponse {
    error: string
}

export interface UnifiedErrorResponse {
    message: string;
    code?: string;
    type: 'GEOCODING_ERROR' | 'NO_ADDRESS_FOUND' | 'NETWORK_ERROR' | 'UNKNOWN_ERROR';
}

export enum RequestLangugage {
    ENGLISH = "en"
}

interface CustomSearchResult<TRawResult = any> {
    // From original search result
    x: number;
    y: number;
    label: string;
    bounds: BoundsTuple | null;
    raw: TRawResult;

    // Custom:
    address: {
        building: string;
        road: string;
        neighbourhood: string;
        suburb: string;
        city_district: string;
        iso3166_2_lvl8: string;
        city: string;
        state_district: string;
        state: string;
        iso3166_2_lvl4: string;
        postcode: string;
        country: string;
        country_code: string;
    }
};

export default class GeosearchUtils {

    public static readonly UNABLE_TO_GEOCODE_ERROR_MESSAGE: string = "Unable to geocode"

    provider = new OpenStreetMapProvider();

    private parseAddress = (response: ParseArgument<RawResult>) => {
        const records = Array.isArray(response.data)
            ? response.data
            : [response.data];

        return records.map((r) => ({
            x: Number(r.lon),
            y: Number(r.lat),
            address: r.address,
            label: r.display_name,
            bounds: [
                [parseFloat(r.boundingbox[0]), parseFloat(r.boundingbox[2])], // s, w
                [parseFloat(r.boundingbox[1]), parseFloat(r.boundingbox[3])], // n, e
            ],
            raw: r,
        }));
    }

    public searchLocation = async (query: string): Promise<SearchResult<RawResult>[]> => {

        console.warn("In GeoUtils search for: ", query);

        const searchArgument: SearchArgument = {
            query: query,
        }

        console.warn("In GeoUtils search argument: ", searchArgument);

        const result = await this.provider.search(searchArgument)

        console.warn("In GeoUtils result: ", result);

        return result
    }

    private searchReverse = async (url: any, parseAddress: any): Promise<CustomSearchResult<RawResult>[] | ErrorResponse> => {
        const response = await fetch(url);
        const jsonRaw = await response.json();
        if (jsonRaw.error) {
            return {
                error: jsonRaw.error
            };
        }

        const json: RequestResult = jsonRaw;
        return parseAddress({ data: json });
    }


    public getAddress = (
        position: LatLng,
        requestLangugage: RequestLangugage,
        setAddressCallback: (searchResult: CustomSearchResult, oneLineAddress: string) => void,
        errorCallback: (error: UnifiedErrorResponse) => void
    ) => {

        console.warn("In GeoUtils getAddress: ", position, requestLangugage);
        const query = {
            "lon": position.lng,
            "lat": position.lat,
        }

        // Generate endpoint - plain url query string  
        let endpoint: string = this.provider.endpoint({ query: query, type: RequestType.REVERSE });

        // Request address in prefered langugage
        endpoint = endpoint.concat("&accept-language=" + requestLangugage)

        // Get reverse (point -> address)
        const searchResultPromise = this.searchReverse(endpoint, this.parseAddress);

        searchResultPromise.then((unknownResult) => {
            if ('error' in unknownResult) {
                const errorResponse: ErrorResponse = unknownResult
                errorCallback({
                    message: errorResponse.error,
                    code: errorResponse.error,
                    type: 'GEOCODING_ERROR'
                })
            } else {
                const result: CustomSearchResult<RawResult>[] = unknownResult
                if (result && result.length > 0) {
                    let localOneLineAddress: string =
                        (result[0].address.building ? (result[0].address.building + ", ") : "") +
                        (result[0].address.road ? (result[0].address.road + ", ") : "") +
                        (result[0].address.neighbourhood ? (result[0].address.neighbourhood + ", ") : "") +
                        (result[0].address.city_district ? (result[0].address.city_district + ", ") : "") +
                        (result[0].address.city ? (result[0].address.city + ", ") : "") +
                        (result[0].address.country_code ? (result[0].address.country_code.toUpperCase() + ", ") : "") +
                        (result[0].address.postcode ? (result[0].address.postcode) : "");

                    localOneLineAddress = localOneLineAddress.trimEnd();
                    localOneLineAddress = localOneLineAddress.endsWith(',') ? localOneLineAddress.substring(0, localOneLineAddress.length - 1) : localOneLineAddress;

                    setAddressCallback(result[0], localOneLineAddress)
                } else {
                    errorCallback({
                        message: "No Address found!",
                        type: 'NO_ADDRESS_FOUND'
                    })
                }
            }
        },
            (e) => {
                console.error("Error in search result promise processing: " + e)
                errorCallback({
                    message: "Error caught: " + e,
                    type: 'NETWORK_ERROR'
                })
            }
        )

    }

}